// i18n fragment - the Tables surface (docs/tables.md): the list view, the grid
// detail view, the create-table + add-field modals, and the logical field-type
// labels. See ../strings.ts for how fragments compose.
import type { LocaleStrings } from './base';

export const tablesStrings: LocaleStrings = {
  en: {
    // list
    'tables.title':            'Tables',
    'tables.new':              'New table',
    'tables.empty_title':      'No tables yet',
    'tables.empty_body':       'Create a table to store structured data your team and agents can read and write.',
    'tables.col_fields':       '{count} fields',
    'tables.owner.workspace':  'Workspace',
    'tables.owner.agent':      'Agent',
    'tables.err.load':         'Could not load tables',

    // create modal
    'tables.create.title':     'New table',
    'tables.create.name':      'Name',
    'tables.create.name_ph':   'e.g. Leads',
    'tables.create.desc':      'Description',
    'tables.create.desc_ph':   'What this table holds (optional)',
    'tables.create.fields':    'Fields',
    'tables.create.add_field': 'Add field',
    'tables.create.submit':    'Create table',
    'tables.create.need_field':'Add at least one field.',

    // field form
    'tables.field.name':         'Field name',
    'tables.field.name_ph':      'e.g. Company',
    'tables.field.type':         'Type',
    'tables.field.required':     'Required',
    'tables.field.unique':       'Unique',
    'tables.field.indexed':      'Indexed',
    'tables.field.link_target':  'Linked table',
    'tables.field.link_ph':      'Choose a table',
    'tables.field.remove':       'Remove field',

    // detail / grid
    'tables.detail.back':        'Tables',
    'tables.detail.add_row':     'Add row',
    'tables.detail.add_field':   'Add field',
    'tables.detail.export':      'Export CSV',
    'tables.detail.export_aria': 'Export {name} as CSV',
    'tables.detail.delete':      'Delete table',
    // draft / save flow
    'tables.detail.unsaved_one': '1 unsaved change',
    'tables.detail.unsaved_many':'{count} unsaved changes',
    'tables.detail.discard':     'Discard',
    'tables.detail.undo_delete': 'Undo delete',
    'tables.detail.leave_title': 'Discard unsaved changes?',
    'tables.detail.leave_body':  'You have {count} unsaved changes. Leave and discard them?',
    'tables.detail.leave_confirm':'Discard and leave',
    'tables.detail.row_count':   '{count} rows',
    'tables.detail.empty':       'No rows yet',
    'tables.detail.empty_body':  'Add a row to start filling this table.',
    'tables.detail.unconfigured':'The Tables feature is not enabled for this workspace yet.',
    'tables.detail.row_actions': 'Row actions',
    'tables.detail.delete_row':  'Delete row',

    // add-field modal
    'tables.add_field.title':    'Add field',
    'tables.add_field.submit':   'Add field',

    // confirms + outcomes
    'tables.delete.title':       'Delete table',
    'tables.delete.body':        'Delete "{name}" and all its rows? This cannot be undone.',
    'tables.delete.confirm':     'Delete table',
    'tables.drop_field.title':   'Delete field',
    'tables.drop_field.body':    'Delete the field "{name}" and its data?',
    'tables.drop_field.confirm': 'Delete field',
    'tables.pending':            'Queued for admin approval.',
    'tables.toast.created':      'Table created',
    'tables.toast.row_added':    'Row added',
    'tables.toast.field_added':  'Field added',
    'tables.toast.deleted':      'Deleted',
    'tables.toast.saved':        'Saved',
    'tables.toast.save_failed':  'Some changes could not be saved',
    'tables.toast.discarded':    'Changes discarded',

    // logical field types (docs/tables.md section 4.1)
    'tables.type.text':           'Text',
    'tables.type.long_text':      'Long text',
    'tables.type.number':         'Number',
    'tables.type.currency':       'Currency',
    'tables.type.percent':        'Percent',
    'tables.type.date':           'Date',
    'tables.type.datetime':       'Date & time',
    'tables.type.boolean':        'Yes / No',
    'tables.type.single_select':  'Single select',
    'tables.type.multi_select':   'Multi select',
    'tables.type.attachment':     'Attachment',
    'tables.type.link_to_record': 'Link to record',
    'tables.type.autonumber':     'Auto number',
    'tables.type.json':           'JSON',
  },
  sv: {
    'tables.title':            'Tabeller',
    'tables.new':              'Ny tabell',
    'tables.empty_title':      'Inga tabeller än',
    'tables.empty_body':       'Skapa en tabell för strukturerad data som ditt team och dina agenter kan läsa och skriva.',
    'tables.col_fields':       '{count} fält',
    'tables.owner.workspace':  'Arbetsyta',
    'tables.owner.agent':      'Agent',
    'tables.err.load':         'Kunde inte läsa in tabeller',

    'tables.create.title':     'Ny tabell',
    'tables.create.name':      'Namn',
    'tables.create.name_ph':   't.ex. Leads',
    'tables.create.desc':      'Beskrivning',
    'tables.create.desc_ph':   'Vad tabellen innehåller (valfritt)',
    'tables.create.fields':    'Fält',
    'tables.create.add_field': 'Lägg till fält',
    'tables.create.submit':    'Skapa tabell',
    'tables.create.need_field':'Lägg till minst ett fält.',

    'tables.field.name':         'Fältnamn',
    'tables.field.name_ph':      't.ex. Företag',
    'tables.field.type':         'Typ',
    'tables.field.required':     'Obligatoriskt',
    'tables.field.unique':       'Unikt',
    'tables.field.indexed':      'Indexerat',
    'tables.field.link_target':  'Länkad tabell',
    'tables.field.link_ph':      'Välj en tabell',
    'tables.field.remove':       'Ta bort fält',

    'tables.detail.back':        'Tabeller',
    'tables.detail.add_row':     'Lägg till rad',
    'tables.detail.add_field':   'Lägg till fält',
    'tables.detail.export':      'Exportera CSV',
    'tables.detail.export_aria': 'Exportera {name} som CSV',
    'tables.detail.delete':      'Ta bort tabell',
    'tables.detail.unsaved_one': '1 osparad ändring',
    'tables.detail.unsaved_many':'{count} osparade ändringar',
    'tables.detail.discard':     'Förkasta',
    'tables.detail.undo_delete': 'Ångra borttagning',
    'tables.detail.leave_title': 'Förkasta osparade ändringar?',
    'tables.detail.leave_body':  'Du har {count} osparade ändringar. Lämna och förkasta dem?',
    'tables.detail.leave_confirm':'Förkasta och lämna',
    'tables.detail.row_count':   '{count} rader',
    'tables.detail.empty':       'Inga rader än',
    'tables.detail.empty_body':  'Lägg till en rad för att börja fylla tabellen.',
    'tables.detail.unconfigured':'Tabellfunktionen är inte aktiverad för den här arbetsytan ännu.',
    'tables.detail.row_actions': 'Radåtgärder',
    'tables.detail.delete_row':  'Ta bort rad',

    'tables.add_field.title':    'Lägg till fält',
    'tables.add_field.submit':   'Lägg till fält',

    'tables.delete.title':       'Ta bort tabell',
    'tables.delete.body':        'Ta bort "{name}" och alla dess rader? Detta kan inte ångras.',
    'tables.delete.confirm':     'Ta bort tabell',
    'tables.drop_field.title':   'Ta bort fält',
    'tables.drop_field.body':    'Ta bort fältet "{name}" och dess data?',
    'tables.drop_field.confirm': 'Ta bort fält',
    'tables.pending':            'Köad för administratörsgodkännande.',
    'tables.toast.created':      'Tabell skapad',
    'tables.toast.row_added':    'Rad tillagd',
    'tables.toast.field_added':  'Fält tillagt',
    'tables.toast.deleted':      'Borttaget',
    'tables.toast.saved':        'Sparat',
    'tables.toast.save_failed':  'Vissa ändringar kunde inte sparas',
    'tables.toast.discarded':    'Ändringar förkastade',

    'tables.type.text':           'Text',
    'tables.type.long_text':      'Lång text',
    'tables.type.number':         'Tal',
    'tables.type.currency':       'Valuta',
    'tables.type.percent':        'Procent',
    'tables.type.date':           'Datum',
    'tables.type.datetime':       'Datum och tid',
    'tables.type.boolean':        'Ja / Nej',
    'tables.type.single_select':  'Enkelval',
    'tables.type.multi_select':   'Flerval',
    'tables.type.attachment':     'Bilaga',
    'tables.type.link_to_record': 'Länk till post',
    'tables.type.autonumber':     'Autonummer',
    'tables.type.json':           'JSON',
  },
};
